import type { AuthTokens, LoginCredentials, LoginResponse, CompleteUser } from '../../types';
import { API_ENDPOINTS } from '../../constants/api';
import { httpClient } from '../api/http-client';

export class AuthService {
  private currentCompleteUser: CompleteUser | null = null;

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await httpClient.post<LoginResponse>(
      API_ENDPOINTS.auth.login,
      credentials,
    );
    
    // Guardar tokens en localStorage
    // El backend envuelve respuestas en { data, statusCode, timestamp }
    const responseData = (response.data as any).data || response.data;
    const { accessToken, refreshToken, usuario } = responseData;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    
    // Guardar usuario completo en localStorage y memoria
    localStorage.setItem('userData', JSON.stringify(usuario));
    this.currentCompleteUser = usuario;
    
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await httpClient.post(API_ENDPOINTS.auth.logout);
    } finally {
      // Limpiar tokens y usuario
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      this.currentCompleteUser = null;
    }
  }

  async getCurrentUser(): Promise<CompleteUser> {
    console.log('🔄 Fetching user profile from /auth/profile');
    const response = await httpClient.get<any>(API_ENDPOINTS.auth.profile);
    
    console.log('📥 Raw response from backend:', response.data);
    
    // El backend puede devolver diferentes estructuras, intentamos extraer los datos correctamente
    let userData: CompleteUser;
    
    if (response.data.usuario) {
      // Si la respuesta tiene estructura {usuario: {...}}
      userData = response.data.usuario;
    } else if (response.data.data && response.data.data.usuario) {
      // Si la respuesta tiene estructura {data: {usuario: {...}}}
      userData = response.data.data.usuario;
    } else if (response.data.data) {
      // Si la respuesta tiene estructura {data: {...}} directamente
      userData = response.data.data;
    } else {
      // Si los datos están directamente en response.data
      userData = response.data;
    }
    
    console.log('👤 Extracted user data:', userData);
    
    if (!userData) {
      throw new Error('No se pudieron extraer los datos del usuario de la respuesta del servidor');
    }
    
    // Actualizar usuario completo en memoria y localStorage
    this.currentCompleteUser = userData;
    localStorage.setItem('userData', JSON.stringify(userData));
    
    console.log('💾 User data saved to localStorage and memory');
    return userData;
  }

  // Nuevo método para obtener el usuario completo con roles y permisos
  getCompleteUser(): CompleteUser | null {
    return this.currentCompleteUser;
  }

  // Método para establecer el usuario completo (útil para persistencia)
  setCompleteUser(user: CompleteUser | null): void {
    this.currentCompleteUser = user;
  }

  async refreshToken(): Promise<AuthTokens> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await httpClient.post<AuthTokens>(
      API_ENDPOINTS.auth.refresh,
      { refreshToken },
    );

    // El backend envuelve respuestas en { data, statusCode, timestamp }
    const responseData = (response.data as any).data || response.data;
    const { accessToken, refreshToken: newRefreshToken } = responseData;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    return response.data;
  }

  async cambiarContrasena(contrasenaActual: string, nuevaContrasena: string): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post(API_ENDPOINTS.auth.changePassword, {
      contrasenaActual,
      nuevaContrasena,
    });
    return (response.data as any).data || response.data;
  }

  async verificarDatosComplementarios(): Promise<{
    tieneDatosComplementarios: boolean;
    completitud: number;
    camposFaltantes: string[];
    datos?: any;
  }> {
    const response = await httpClient.get(API_ENDPOINTS.auth.surveyStatus);
    return (response.data as any).data || response.data;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}

export const authService = new AuthService();